import React, { useRef, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Button, IconButton, Tooltip, Card, CardContent, Divider, Chip, List, ListItem, ListItemText, Table, TableHead, TableBody, TableRow, TableCell, TableContainer } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import useStore from "src/v2/utils/store";
import { useAlertStore } from "src/v2/utils/alert-store";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    fontSize: '1.2rem',
  },
  actions: {
    display: "flex",
    alignItems: "center",
  },
  downloadButton: {
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    padding: theme.spacing(0.5, 1),
    fontSize: '0.85rem',
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    fontSize: '1rem',
  },
  sectionIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1.1rem',
  },
  card: {
    marginBottom: theme.spacing(1),
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[3],
    },
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  roleChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 500,
    height: 24,
    fontSize: '0.75rem',
  },
  eventChip: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    fontWeight: 500,
    height: 24,
    fontSize: '0.75rem',
  },
  requirementsList: {
    paddingLeft: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    marginBottom: 0,
    paddingBottom: 0,
  },
  requirementItem: {
    display: "list-item",
    listStyleType: "disc",
    marginLeft: theme.spacing(1),
    paddingTop: 0,
    paddingBottom: 0,
  },
  noSpecification: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  regenerateButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.primary.main,
    padding: 6,
  },
  openQuestion: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.action.hover,
    borderLeft: `3px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius,
  },
  divider: {
    margin: theme.spacing(1.5, 0),
  },
  cardContent: {
    padding: theme.spacing(1.5),
    "&:last-child": {
      paddingBottom: theme.spacing(1.5),
    },
  },
  listItemText: {
    margin: 0,
  },
  actionDescription: {
    fontSize: '0.95rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  requirementText: {
    fontSize: '0.85rem',
  },
  "@media print": {
    actions: {
      display: "none",
    },
    paper: {
      boxShadow: "none",
      padding: 0,
    },
    header: {
      marginBottom: theme.spacing(1),
    },
    card: {
      boxShadow: "none",
      border: `1px solid ${theme.palette.divider}`,
      pageBreakInside: "avoid",
    },
    body: { 
      fontSize: 12, 
    },
    h1: { 
      fontSize: 16, 
    },
    h2: { 
      fontSize: 14, 
    },
    h3: { 
      fontSize: 14,
    },
  },
}));

export default function FormalSpecification() {
  const classes = useStyles();
  const oneReview = useStore((s) => s.oneReview);
  const regenerateFormalSpecification = useStore((s) => s.regenerateFormalSpecification);
  const createLog = useStore((s) => s.createLog);
  const showAlert = useAlertStore((s) => s.showAlert);
  const contentRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Parse the JSON specification
  const parseSpecification = () => {
    if (!oneReview?.formalSpecification) return null;
    
    try {
      return JSON.parse(oneReview.formalSpecification);
    } catch (error) {
      console.error("Error parsing formal specification JSON:", error);
      return null;
    }
  };
  
  const specification = parseSpecification();

  const handleDownloadAsPdf = async () => {
    if (!contentRef.current || !oneReview?._id) return;

    setIsGeneratingPdf(true);
    
    try {
      const reviewName = oneReview?.name || "formal-specification";
      const filename = `${reviewName.replace(/\s+/g, "-")}-specification.pdf`;
      
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        throw new Error("Pop-up blocked. Please allow pop-ups for this site.");
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${filename}</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
                color: #333;
                line-height: 1.4;
                margin: 15px;
                padding: 0;
              }
              h1 { color: #2c3e50; margin-top: 15px; margin-bottom: 10px; font-size: 18pt; }
              h2 { color: #3498db; margin-top: 10px; margin-bottom: 8px; font-size: 16pt; }
              h3 { color: #2980b9; margin-top: 8px; margin-bottom: 5px; font-size: 14pt; }
              p { margin: 5px 0; }
              .card {
                border: 1px solid #ddd;
                border-radius: 4px;
                padding: 10px;
                margin-bottom: 10px;
              }
              .chip {
                display: inline-block;
                padding: 3px 6px;
                background-color: #3498db;
                color: white;
                border-radius: 12px;
                font-size: 0.8em;
                font-weight: bold;
                margin-bottom: 5px;
              }
              .event-chip {
                background-color: #9b59b6;
              }
              .section-header {
                display: flex;
                align-items: center;
                margin-top: 12px;
                margin-bottom: 8px;
                color: #2980b9;
              }
              .requirements-list {
                padding-left: 15px;
                margin: 5px 0;
              }
              .requirement-item {
                display: list-item;
                list-style-type: disc;
                margin-left: 12px;
                margin-bottom: 3px;
              }
              .open-question {
                border-left: 4px solid #f39c12;
                padding: 6px 10px;
                background-color: #fff9e6;
                margin: 10px 0;
              }
              .divider {
                border-top: 1px solid #eee;
                margin: 10px 0;
              }
              .spec-table {
                width: 100%;
                border-collapse: collapse;
                margin: 8px 0 16px 0;
              }
              .spec-table th, .spec-table td {
                border: 1px solid #999;
                padding: 6px 8px;
                text-align: left;
                vertical-align: top;
                font-size: 10pt;
              }
              .spec-table th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              @media print {
                body { font-size: 10pt; }
                h1 { font-size: 14pt; }
                h2 { font-size: 12pt; }
                h3 { font-size: 11pt; }
                .card { break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <h1>Formal Specification</h1>
            <h2>Actors and Actions</h2>
            ${specification?.actorsAndActions?.length ? `
              <table class="spec-table">
                <thead>
                  <tr>
                    <th>Actor / Role</th>
                    <th>Action he can perform</th>
                    <th>Action input</th>
                    <th>Action conditions</th>
                  </tr>
                </thead>
                <tbody>
                  ${specification.actorsAndActions.map(item => `
                    <tr>
                      <td>${item.role || ''}</td>
                      <td>${item.action || ''}</td>
                      <td>${item.input || 'N/A'}</td>
                      <td>${item.conditions || 'N/A'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : '<p>No actor and action specifications available</p>'}
            
            <div class="divider"></div>
            
            <h2>Events and Triggers</h2>
            ${specification?.eventsAndTriggers?.length ? `
              <table class="spec-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Description</th>
                    <th>Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  ${specification.eventsAndTriggers.map(item => `
                    <tr>
                      <td>${item.event || ''}</td>
                      <td>${item.description || ''}</td>
                      <td>${item.trigger || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : '<p>No event specifications available</p>'}
            
            ${specification?.components?.length ? `
              <div class="divider"></div>
              <h2>System Components</h2>
              <table class="spec-table">
                <thead><tr><th>Component</th><th>Responsibility</th></tr></thead>
                <tbody>
                  ${specification.components.map(item => `
                    <tr><td>${item.name || ''}</td><td>${item.responsibility || ''}</td></tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
            
            ${specification?.dataModel?.length ? `
              <div class="divider"></div>
              <h2>Data Model / Storage</h2>
              <table class="spec-table">
                <thead><tr><th>Variable</th><th>Type</th><th>Visibility</th><th>Purpose</th></tr></thead>
                <tbody>
                  ${specification.dataModel.map(item => `
                    <tr>
                      <td>${item.variable || ''}</td>
                      <td>${item.type || ''}</td>
                      <td>${item.visibility || ''}</td>
                      <td>${item.purpose || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
            
            ${specification?.invariants?.length ? `
              <div class="divider"></div>
              <h2>Invariants</h2>
              <table class="spec-table">
                <thead><tr><th>ID</th><th>Statement</th></tr></thead>
                <tbody>
                  ${specification.invariants.map(item => `
                    <tr><td>${item.id || ''}</td><td>${item.statement || ''}</td></tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
            
            ${specification?.trustBoundaries?.length ? `
              <div class="divider"></div>
              <h2>Trust Boundaries &amp; Roles</h2>
              <table class="spec-table">
                <thead><tr><th>Boundary</th><th>Inside</th><th>Outside</th></tr></thead>
                <tbody>
                  ${specification.trustBoundaries.map(item => `
                    <tr>
                      <td>${item.boundary || ''}</td>
                      <td>${item.inside || ''}</td>
                      <td>${item.outside || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
            
            ${specification?.threats?.length ? `
              <div class="divider"></div>
              <h2>Threats &amp; Mitigations</h2>
              <table class="spec-table">
                <thead><tr><th>Threat</th><th>Asset</th><th>Mitigation</th></tr></thead>
                <tbody>
                  ${specification.threats.map(item => `
                    <tr>
                      <td>${item.threat || ''}</td>
                      <td>${item.asset || ''}</td>
                      <td>${item.mitigation || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
            
            ${specification?.risks?.length ? `
              <div class="divider"></div>
              <h2>Architectural Risks</h2>
              <table class="spec-table">
                <thead><tr><th>ID</th><th>Risk</th><th>Likelihood</th><th>Impact</th><th>Mitigation</th></tr></thead>
                <tbody>
                  ${specification.risks.map(item => `
                    <tr>
                      <td>${item.id || ''}</td>
                      <td>${item.risk || ''}</td>
                      <td>${item.likelihood || ''}</td>
                      <td>${item.impact || ''}</td>
                      <td>${item.mitigation || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
            
            ${specification?.deployment?.length ? `
              <div class="divider"></div>
              <h2>Deployment Topology</h2>
              <table class="spec-table">
                <thead><tr><th>Environment</th><th>Network</th><th>Purpose</th></tr></thead>
                <tbody>
                  ${specification.deployment.map(item => `
                    <tr>
                      <td>${item.environment || ''}</td>
                      <td>${item.network || ''}</td>
                      <td>${item.purpose || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
            
            ${specification?.openQuestions?.length ? `
              <div class="divider"></div>
              <h2>Open Questions</h2>
              ${specification.openQuestions.map(question => `
                <div class="open-question">${question}</div>
              `).join('')}
            ` : ''}
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 1000);
              };
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      showAlert({
        message: "Print dialog opened in a new tab. Save as PDF to download the document.",
        severity: "info",
        title: "Print to PDF",
      });
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      showAlert({
        message: error.message || "Could not generate PDF. Please try printing directly.",
        severity: "error",
        title: "PDF Error",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleRegenerateSpec = async () => {
    if (!oneReview?._id) return;
    
    setIsRegenerating(true);
    createLog({
      event_type: 'FormalSpecification.RegenerateRequested',
      operation: 'Regenerate Formal Specification',
      review_id: oneReview._id,
    });
    try {
      await regenerateFormalSpecification(oneReview._id, showAlert);
    } catch (error) {
      console.error("Error regenerating specification:", error);
      createLog({
        event_type: 'FormalSpecification.RegenerateRequested',
        operation: 'Regenerate Formal Specification',
        review_id: oneReview._id,
        result: 'failure',
        http_status: 500,
        error_message: error && error.message,
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  if (!oneReview) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.noSpecification}>
          <Typography variant="h6">No review selected</Typography>
          <Typography variant="body2">
            Please select a review to view its formal specification.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!oneReview.formalSpecification) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.noSpecification}>
          <Typography variant="h6">No Formal Specification Available</Typography>
          <Typography variant="body2">
            This review does not have a formal specification document.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!specification) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.noSpecification}>
          <Typography variant="h6">Invalid Specification Format</Typography>
          <Typography variant="body2">
            The formal specification could not be parsed correctly. Please regenerate it.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRegenerateSpec}
            style={{ marginTop: 16 }}
            disabled={isRegenerating}
            size="small"
          >
            {isRegenerating ? "Regenerating..." : "Regenerate Specification"}
          </Button>
        </Box>
      </Paper>
    );
  }

  // Add a special case for when we have a valid JSON but no actual specification content
  if (
    specification && 
    specification.actorsAndActions && 
    specification.actorsAndActions.length === 0 && 
    specification.eventsAndTriggers && 
    specification.eventsAndTriggers.length === 0 && 
    specification.openQuestions && 
    specification.openQuestions.length > 0
  ) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Typography variant="h6" className={classes.title}>
            Formal Specification
          </Typography>
          <Box className={classes.actions}>
            <Tooltip title="Regenerate Specification">
              <Button
                variant="contained"
                color="primary"
                onClick={handleRegenerateSpec}
                disabled={isRegenerating}
                size="small"
                startIcon={isRegenerating ? <CircularProgress size={20} /> : <RefreshIcon />}
              >
                {isRegenerating ? "Regenerating..." : "Regenerate"}
              </Button>
            </Tooltip>
          </Box>
        </Box>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: "#FFF9C4", borderRadius: 1 }}>
          <Typography variant="h6" style={{ color: "#F57C00", marginBottom: 8 }}>
            Unable to Create Formal Specification
          </Typography>
          
          {specification.openQuestions.map((question, index) => (
            <Typography key={index} variant="body2" paragraph style={{ marginBottom: 8 }}>
              {question}
            </Typography>
          ))}
          
          <Typography variant="body2" style={{ marginTop: 16 }}>
            Please check that your architecture and specification documents contain sufficient information about actors, roles, permissions, and events in the smart contract.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <div className={classes.root} ref={contentRef}>
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Typography variant="h6" className={classes.title}>
            Formal Specification
          </Typography>
          <Box className={classes.actions}>
            <Tooltip title="Download as PDF">
              <Button
                variant="contained"
                size="small"
                className={classes.downloadButton}
                startIcon={<GetAppIcon />}
                onClick={handleDownloadAsPdf}
                disabled={isGeneratingPdf || isRegenerating}
              >
                {isGeneratingPdf ? "Generating..." : "PDF"}
              </Button>
            </Tooltip>
            <Tooltip title="Regenerate Specification">
              <IconButton
                className={classes.regenerateButton}
                onClick={handleRegenerateSpec}
                disabled={isGeneratingPdf || isRegenerating}
                size="small"
              >
                {isRegenerating ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {(isGeneratingPdf || isRegenerating) ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={32} />
            <Typography variant="body2" style={{ marginTop: 12, fontSize: '0.85rem' }}>
              {isGeneratingPdf ? "Generating PDF..." : "Regenerating specification..."}
            </Typography>
          </Box>
        ) : (
          <div>
            {/* Actors and Actions Section */}
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Actors and Actions
            </Typography>
            
            {specification.actorsAndActions && specification.actorsAndActions.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                <Table size="small" aria-label="actors and actions">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "18%" }}>Actor / Role</TableCell>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "30%" }}>Action he can perform</TableCell>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "26%" }}>Action input</TableCell>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "26%" }}>Action conditions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {specification.actorsAndActions.map((item, index) => (
                      <TableRow key={`action-${index}`}>
                        <TableCell>{item.role}</TableCell>
                        <TableCell>{item.action}</TableCell>
                        <TableCell>{item.input || "N/A"}</TableCell>
                        <TableCell>{item.conditions || "N/A"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No actor and action specifications available
              </Typography>
            )}
            
            <Divider className={classes.divider} />
            
            {/* Events and Triggers Section */}
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Events and Triggers
            </Typography>
            
            {specification.eventsAndTriggers && specification.eventsAndTriggers.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                <Table size="small" aria-label="events and triggers">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "22%" }}>Event</TableCell>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "40%" }}>Description</TableCell>
                      <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "38%" }}>Trigger</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {specification.eventsAndTriggers.map((item, index) => (
                      <TableRow key={`event-${index}`}>
                        <TableCell>{item.event}</TableCell>
                        <TableCell>{item.description || ""}</TableCell>
                        <TableCell>{item.trigger || ""}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No event specifications available
              </Typography>
            )}
            
            {/* Architecture-only sections — only render when present in AI output */}
            {specification.components && specification.components.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  System Components
                </Typography>
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                  <Table size="small" aria-label="system components">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "30%" }}>Component</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "70%" }}>Responsibility</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specification.components.map((item, index) => (
                        <TableRow key={`component-${index}`}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.responsibility}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {specification.dataModel && specification.dataModel.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Data Model / Storage
                </Typography>
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                  <Table size="small" aria-label="data model">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "20%" }}>Variable</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "25%" }}>Type</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "15%" }}>Visibility</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "40%" }}>Purpose</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specification.dataModel.map((item, index) => (
                        <TableRow key={`data-${index}`}>
                          <TableCell>{item.variable}</TableCell>
                          <TableCell>{item.type}</TableCell>
                          <TableCell>{item.visibility}</TableCell>
                          <TableCell>{item.purpose}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {specification.invariants && specification.invariants.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Invariants
                </Typography>
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                  <Table size="small" aria-label="invariants">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "15%" }}>ID</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "85%" }}>Statement</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specification.invariants.map((item, index) => (
                        <TableRow key={`inv-${index}`}>
                          <TableCell>{item.id}</TableCell>
                          <TableCell>{item.statement}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {specification.trustBoundaries && specification.trustBoundaries.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Trust Boundaries &amp; Roles
                </Typography>
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                  <Table size="small" aria-label="trust boundaries">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "30%" }}>Boundary</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "35%" }}>Inside</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "35%" }}>Outside</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specification.trustBoundaries.map((item, index) => (
                        <TableRow key={`tb-${index}`}>
                          <TableCell>{item.boundary}</TableCell>
                          <TableCell>{item.inside}</TableCell>
                          <TableCell>{item.outside}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {specification.threats && specification.threats.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Threats &amp; Mitigations
                </Typography>
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                  <Table size="small" aria-label="threats">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "30%" }}>Threat</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "25%" }}>Asset</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "45%" }}>Mitigation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specification.threats.map((item, index) => (
                        <TableRow key={`threat-${index}`}>
                          <TableCell>{item.threat}</TableCell>
                          <TableCell>{item.asset}</TableCell>
                          <TableCell>{item.mitigation}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {specification.risks && specification.risks.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Architectural Risks
                </Typography>
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                  <Table size="small" aria-label="risks">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "8%" }}>ID</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "32%" }}>Risk</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "12%" }}>Likelihood</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "12%" }}>Impact</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "36%" }}>Mitigation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specification.risks.map((item, index) => (
                        <TableRow key={`risk-${index}`}>
                          <TableCell>{item.id}</TableCell>
                          <TableCell>{item.risk}</TableCell>
                          <TableCell>{item.likelihood}</TableCell>
                          <TableCell>{item.impact}</TableCell>
                          <TableCell>{item.mitigation}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {specification.deployment && specification.deployment.length > 0 && (
              <>
                <Divider className={classes.divider} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Deployment Topology
                </Typography>
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16 }}>
                  <Table size="small" aria-label="deployment">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "25%" }}>Environment</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "30%" }}>Network</TableCell>
                        <TableCell style={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: "45%" }}>Purpose</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {specification.deployment.map((item, index) => (
                        <TableRow key={`dep-${index}`}>
                          <TableCell>{item.environment}</TableCell>
                          <TableCell>{item.network}</TableCell>
                          <TableCell>{item.purpose}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {/* Open Questions Section */}
            {specification.openQuestions && specification.openQuestions.length > 0 && (
              <>
                <Divider className={classes.divider} />
                
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Open Questions
                </Typography>
                
                {specification.openQuestions.map((question, index) => (
                  <Box key={`question-${index}`} className={classes.openQuestion}>
                    <Typography variant="body2" className={classes.requirementText}>
                      {question}
                    </Typography>
                  </Box>
                ))}
              </>
            )}
          </div>
        )}
      </Paper>
    </div>
  );
}